//Built-in libraries

//Third party libraries
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useDispatch } from 'react-redux';
//Services
import { postWorkspace, putWorkspace } from '../../api/Terminal/Workspace';
//Assets andd Miscellaneous
import { ReactQueryKey } from '../../constants/ReactQueryKeys';
import { notifyUser } from '../../store/Toast';
import type { IWorkspace } from '../../types/Workspace';
import { sanitizeResponseError } from '../../utils';
//Custom components
import Button from '../UI/Button';
import { HookForm } from '../UI/HookForm/Form';
import { HookInput } from '../UI/HookForm/Form/Input';
import { HookFormProvider } from '../UI/HookForm/Provider';

//Component Begin
interface IWorkspaceFormProps {
  handleSubmit: () => void;
  isWorkspaceListUnavailable?: boolean;
  onCancel: () => void;
  workspace: IWorkspace | undefined;
  workspaces?: IWorkspace[];
}
const normalizeProgramName = (name: string) => name.trim().toLocaleLowerCase();
const formValueToString = (value: unknown) => String(value ?? '');

const programNameExists = (name: string, workspaces: IWorkspace[], workspace?: IWorkspace) =>
  workspaces.some((item) => item.id !== workspace?.id && normalizeProgramName(item.name) === normalizeProgramName(name));

export const WorkspaceForm = ({
  workspace,
  workspaces = [],
  isWorkspaceListUnavailable = false,
  onCancel,
  handleSubmit,
}: IWorkspaceFormProps) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [serverError, setServerError] = useState<string>();
  const nameInputId = useId();
  const descriptionInputId = useId();

  const createWorkspace = useMutation((data: IPostWorkspacePayload) => postWorkspace({ data }), {
    onSuccess: () => {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Program Successfully Created.',
          toastType: 'success',
        })
      );
      queryClient.invalidateQueries([ReactQueryKey.GET_WORKSPACES]);
      handleSubmit();
    },
    onError: (error: unknown) => {
      const resError = sanitizeResponseError(error);
      setServerError(resError);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: `${resError}`,
          toastType: 'failed',
        })
      );
    },
  });
  const updateWorkspace = useMutation((data: IPutWorkspacePayload) => putWorkspace({ data, workspaceId: Number(workspace?.id) }), {
    onSuccess: () => {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Program Updated Successfully.',
          toastType: 'success',
        })
      );
      queryClient.invalidateQueries([ReactQueryKey.GET_WORKSPACES]);
      handleSubmit();
    },
    onError: (error: unknown) => {
      const resError = sanitizeResponseError(error);
      setServerError(resError);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: resError,
          toastType: 'failed',
        })
      );
    },
  });

  const onSubmit = (data: IWorkspace) => {
    if (createWorkspace.isLoading || updateWorkspace.isLoading) return;
    setServerError(undefined);
    const payload: IPostWorkspacePayload = {
      name: data.name.trim(),
      description: data.description?.trim(),
    };

    if (workspace) {
      const updatedWorkspace: IPutWorkspacePayload = {
        ...payload,
      };
      updateWorkspace.mutate(updatedWorkspace);
    } else {
      createWorkspace.mutate(payload);
    }
  };
  const isSubmitting = createWorkspace.isLoading || updateWorkspace.isLoading;

  return (
    <HookFormProvider defaultValues={{ name: '', description: '', ...workspace }}>
      <h3 className='mb-2 text-lg text-primaryColor-normal'>{workspace ? 'Update Program' : 'Create New Program'}</h3>
      <HookForm onSubmit={onSubmit} className='space-y-5'>
        <HookInput
          type='text'
          placeholder='Program Name'
          required
          label={'Name'}
          name='name'
          className='flex-grow'
          id={nameInputId}
          maxLength={50}
          validate={(value: unknown) => {
            const trimmedValue = formValueToString(value).trim();
            if (!trimmedValue) return 'Program name is required';
            if (trimmedValue.length > 50) return 'Program name must be 50 characters or fewer';
            if (programNameExists(trimmedValue, workspaces, workspace)) return 'A program with this name already exists.';
            return true;
          }}
        />
        <span className='text-xs'>50 Characters Maximum</span>
        <HookInput
          type='text'
          placeholder='Program Description'
          label='Description'
          className='flex-grow'
          name='description'
          id={descriptionInputId}
          maxLength={50}
          validate={(value: unknown) => {
            if (formValueToString(value).trim().length > 50) return 'Program description must be 50 characters or fewer';
            return true;
          }}
        />
        <span className='text-xs'>50 Characters Maximum</span>
        {isWorkspaceListUnavailable && (
          <p className='rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800' role='status'>
            Some existing programs could not be loaded. You can still submit; duplicate checks will be confirmed by the server.
          </p>
        )}
        {serverError && (
          <p className='rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700' role='alert'>
            {serverError}
          </p>
        )}
        <div className='flex justify-end mt-4 gap-2'>
          <Button label='Cancel' btnStyle='btn-neutral' type='button' onClick={onCancel} disabled={isSubmitting} />
          <Button
            label={isSubmitting ? 'Saving...' : workspace ? 'Save' : 'Create'}
            btnStyle='btn-primary'
            type='submit'
            disabled={isSubmitting}
          />
        </div>
      </HookForm>
    </HookFormProvider>
  );
};
